/** @type {import('tailwindcss').Config} */

const plugin = require('tailwindcss/plugin')
const defaultTheme = require('tailwindcss/defaultTheme')

module.exports = {
  content: [
    "./index.php",
    "./app/**/*.php",
    "./resources/**/*.{php,vue,js}",
    "./config/acf.php"
  ],
  theme: {
    screens: {
      'xs': '400px',
      ...defaultTheme.screens
    },
    container: {
        center: true,
        padding: '2rem',
        screens: {
          sm: '1102px',
          md: '1102px',
          lg: '1102px',
          xl: '1102px',
          '2xl': '1102px'
        }
    },
    colors: {
        primary: '#f0572a',
        white: '#ffffff',
        black: '#000000',
        gray: {
            '100': '#f1f1f2',
            '200': '#dee1e3', 
            '300': '#c1c6ca', 
            '400': '#9da3a9', 
            '500': '#62666a', 
            '600': '#3c3f42', 
            '700': '#2c2f32'
        },
        current: 'currentColor',
        transparent: 'transparent'
    },
    fontFamily: {
      'base': 'Roboto, sans-serif',
      'condensed': '"Roboto Condensed", sans-serif',
      'title': '"Technor Variable", serif'
    },
    fontSize: {
      '8xl': ['5.625rem', {
          lineHeight: '0.8888888889',
          fontWeight: '700'
      }],
      '7xl': ['4.375rem', {
        lineHeight: '1',
        fontWeight: '700',
      }],
      '6xl': ['3.125rem', {
        lineHeight: '1.1'
      }],
      '5xl': ['2.5rem', {
        lineHeight: '1.125'
      }],
      '4xl': ['2rem', {
        lineHeight: '1.125'
      }],
      '3xl': ['1.5rem', {
        lineHeight: '1.25'
      }],
      '2xl': ['1.375rem', {
        lineHeight: '1.5'
      }],
      xl: ['1.25rem', {
        lineHeight: '1.4'
      }],
      lg: ['1.125rem', {
        lineHeight: '1.5'
      }],
      base: ['1rem', {
        lineHeight: '1.5'
      }],
      sm: ['0.875rem', {
        lineHeight: '1.5'
      }],
      xs: ['0.75rem', {
        lineHeight: '1.5'
      }],
    },
    mask: {
      'top-left': ['url(@/assets/images/mask-top-left.svg)', {
        '--tw-mask-position-x': 0,
        '--tw-mask-position-y': 0
      }],
      'top-right': ['url(@/assets/images/mask-top-right.svg)', {
        '--tw-mask-position-x': '100%',
        '--tw-mask-position-y': 0
      }],
      'bottom-left': ['url(@/assets/images/mask-bottom-left.svg)', {
        '--tw-mask-position-x': 0,
        '--tw-mask-position-y': '100%'
      }],
      'bottom-right': ['url(@/assets/images/mask-bottom-right.svg)', {
        '--tw-mask-position-x': '100%',
        '--tw-mask-position-y': '100%'
      }],
    },
    maskClip: {
      'border': 'border-box',
      'padding': 'padding-box',
      'content': 'content-box'
    },
    textShadow: {
      DEFAULT: '0px 0px 39px rgba(0, 0, 0, 0.75)'
    },
    extend: {
      animation: {
        'bounce-side': 'bounce-side 800ms ease-in-out'
      },
      keyframes: {
        'bounce-side': {
          '0%, 100%': { 'transform': 'translateX(0)' },
          '30%': { 'transform': 'translateX(30%)' },
        }
      },
      aspectRatio: {
        'portrait': '9/16'
      },
      backgroundImage: {
        'pattern': 'url(@/assets/images/pattern.svg)',
        'selection': 'var(--tw-image-selection)',
        'overlay-only': 'linear-gradient(180deg, rgba(0, 0, 0, 0.71) 0%, rgba(0, 0, 0, 0.5) 44.82%, #000000 90.9%)',
        'overlay-2': 'linear-gradient(to bottom, var(--tw-gradient-stops)), var(--tw-image-selection)',
        'overlay': 'linear-gradient(180deg, rgba(0, 0, 0, 0.71) 0%, rgba(0, 0, 0, 0.5) 44.82%, #000000 90.9%), var(--tw-image-selection)',
        'split-x': 'linear-gradient(to right, var(--tw-gradient-from), var(--tw-gradient-from) 50%, var(--tw-gradient-to) 50%, var(--tw-gradient-to))'
      },
      typography: ({ theme }) => ({
        DEFAULT: {
          css: {
            headings: {
              lineHeight: 1.1
            },
            'h1,h2,h3,h4, .alignleft h2': {
              'marginTop': '1em',
              'marginBottom': '0.6em',
              lineHeight: 1.1
            },
            '.alignleft h2': {
              'marginTop': '1em !important',
              'marginBottom': '0.6em !important'
            },
            '.wp-block-buttons': {
              'display': 'flex',
              'gap': theme('gap.4')
            },
            maxWidth: 'none',
            '--tw-prose-body': theme('colors.black'),
            '--tw-prose-headings': theme('colors.black'),
            '--tw-prose-lead': theme('colors.black'),
            '--tw-prose-links': theme('colors.black'),
            '--tw-prose-bold': theme('colors.black'),
            '--tw-prose-counters': theme('colors.black'),
            '--tw-prose-bullets': theme('colors.black'),
            '--tw-prose-hr': theme('colors.black'),
            '--tw-prose-quotes': theme('colors.black'),
            '--tw-prose-quote-borders': theme('colors.black'),
            '--tw-prose-captions': theme('colors.black'),
            '--tw-prose-code': theme('colors.black'),
            '--tw-prose-pre-code': theme('colors.black'),
            '--tw-prose-pre-bg': theme('colors.black'),
            '--tw-prose-th-borders': theme('colors.black'),
            '--tw-prose-td-borders': theme('colors.black'),
            '--tw-prose-invert-body': theme('colors.black'),
            '--tw-prose-invert-headings': theme('colors.white'),
            '--tw-prose-invert-lead': theme('colors.black'),
            '--tw-prose-invert-links': theme('colors.white'),
            '--tw-prose-invert-bold': theme('colors.white'),
            '--tw-prose-invert-counters': theme('colors.black'),
            '--tw-prose-invert-bullets': theme('colors.black'),
            '--tw-prose-invert-hr': theme('colors.black'),
            '--tw-prose-invert-quotes': theme('colors.black'),
            '--tw-prose-invert-quote-borders': theme('colors.black'),
            '--tw-prose-invert-captions': theme('colors.black'),
            '--tw-prose-invert-code': theme('colors.white'),
            '--tw-prose-invert-pre-code': theme('colors.black'),
            '--tw-prose-invert-pre-bg': 'rgb(0 0 0 / 50%)',
            '--tw-prose-invert-th-borders': theme('colors.black'),
            '--tw-prose-invert-td-borders': theme('colors.black'),
          },
        },
        white: {
          css: {
            '--tw-prose-body': theme('colors.white'),
            '--tw-prose-headings': theme('colors.white'),
            '--tw-prose-lead': theme('colors.white'),
            '--tw-prose-links': theme('colors.white'),
            '--tw-prose-bold': theme('colors.white'),
            '--tw-prose-counters': theme('colors.white'),
            '--tw-prose-bullets': theme('colors.white'),
            '--tw-prose-hr': theme('colors.white'),
            '--tw-prose-quotes': theme('colors.white'),
            '--tw-prose-quote-borders': theme('colors.white'),
            '--tw-prose-captions': theme('colors.white'),
            '--tw-prose-code': theme('colors.white'),
            '--tw-prose-pre-code': theme('colors.white'),
            '--tw-prose-pre-bg': theme('colors.white'),
            '--tw-prose-th-borders': theme('colors.white'),
            '--tw-prose-td-borders': theme('colors.white'),
            '--tw-prose-invert-body': theme('colors.white'),
            '--tw-prose-invert-headings': theme('colors.white'),
            '--tw-prose-invert-lead': theme('colors.white'),
            '--tw-prose-invert-links': theme('colors.black'),
            '--tw-prose-invert-bold': theme('colors.black'),
            '--tw-prose-invert-counters': theme('colors.white'),
            '--tw-prose-invert-bullets': theme('colors.white'),
            '--tw-prose-invert-hr': theme('colors.white'),
            '--tw-prose-invert-quotes': theme('colors.white'),
            '--tw-prose-invert-quote-borders': theme('colors.white'),
            '--tw-prose-invert-captions': theme('colors.white'),
            '--tw-prose-invert-code': theme('colors.white'),
            '--tw-prose-invert-pre-code': theme('colors.white'),
            '--tw-prose-invert-pre-bg': 'rgb(0 0 0 / 50%)',
            '--tw-prose-invert-th-borders': theme('colors.white'),
            '--tw-prose-invert-td-borders': theme('colors.white'),
          },
        },
        light: {
          css: {
            '--tw-prose-body': theme('colors.gray.100'),
            '--tw-prose-headings': theme('colors.gray.100'),
            '--tw-prose-lead': theme('colors.gray.100'),
            '--tw-prose-links': theme('colors.gray.100'),
            '--tw-prose-bold': theme('colors.gray.100'),
            '--tw-prose-counters': theme('colors.gray.100'),
            '--tw-prose-bullets': theme('colors.gray.100'),
            '--tw-prose-hr': theme('colors.gray.100'),
            '--tw-prose-quotes': theme('colors.gray.100'),
            '--tw-prose-quote-borders': theme('colors.gray.100'),
            '--tw-prose-captions': theme('colors.gray.100'),
            '--tw-prose-code': theme('colors.gray.100'),
            '--tw-prose-pre-code': theme('colors.gray.100'),
            '--tw-prose-pre-bg': theme('colors.gray.100'),
            '--tw-prose-th-borders': theme('colors.gray.100'),
            '--tw-prose-td-borders': theme('colors.gray.100'),
            '--tw-prose-invert-body': theme('colors.gray.100'),
            '--tw-prose-invert-headings': theme('colors.gray.100'),
            '--tw-prose-invert-lead': theme('colors.gray.100'),
            '--tw-prose-invert-links': theme('colors.black'),
            '--tw-prose-invert-bold': theme('colors.black'),
            '--tw-prose-invert-counters': theme('colors.gray.100'),
            '--tw-prose-invert-bullets': theme('colors.gray.100'),
            '--tw-prose-invert-hr': theme('colors.gray.100'),
            '--tw-prose-invert-quotes': theme('colors.gray.100'),
            '--tw-prose-invert-quote-borders': theme('colors.gray.100'),
            '--tw-prose-invert-captions': theme('colors.gray.100'),
            '--tw-prose-invert-code': theme('colors.gray.100'),
            '--tw-prose-invert-pre-code': theme('colors.gray.100'),
            '--tw-prose-invert-pre-bg': 'rgb(0 0 0 / 50%)',
            '--tw-prose-invert-th-borders': theme('colors.gray.100'),
            '--tw-prose-invert-td-borders': theme('colors.gray.100'),
          },
        },
        faint: {
          css: {
            '--tw-prose-body': theme('colors.gray.300'),
            '--tw-prose-headings': theme('colors.gray.300'),
            '--tw-prose-lead': theme('colors.gray.300'),
            '--tw-prose-links': theme('colors.gray.300'),
            '--tw-prose-bold': theme('colors.gray.300'),
            '--tw-prose-counters': theme('colors.gray.300'),
            '--tw-prose-bullets': theme('colors.gray.300'),
            '--tw-prose-hr': theme('colors.gray.300'),
            '--tw-prose-quotes': theme('colors.gray.300'),
            '--tw-prose-quote-borders': theme('colors.gray.300'),
            '--tw-prose-captions': theme('colors.gray.300'),
            '--tw-prose-code': theme('colors.gray.300'),
            '--tw-prose-pre-code': theme('colors.gray.300'),
            '--tw-prose-pre-bg': theme('colors.gray.300'),
            '--tw-prose-th-borders': theme('colors.gray.300'),
            '--tw-prose-td-borders': theme('colors.gray.300'),
            '--tw-prose-invert-body': theme('colors.gray.300'),
            '--tw-prose-invert-headings': theme('colors.gray.300'),
            '--tw-prose-invert-lead': theme('colors.gray.300'),
            '--tw-prose-invert-links': theme('colors.black'),
            '--tw-prose-invert-bold': theme('colors.black'),
            '--tw-prose-invert-counters': theme('colors.gray.300'),
            '--tw-prose-invert-bullets': theme('colors.gray.300'),
            '--tw-prose-invert-hr': theme('colors.gray.300'),
            '--tw-prose-invert-quotes': theme('colors.gray.300'),
            '--tw-prose-invert-quote-borders': theme('colors.gray.300'),
            '--tw-prose-invert-captions': theme('colors.gray.300'),
            '--tw-prose-invert-code': theme('colors.gray.300'),
            '--tw-prose-invert-pre-code': theme('colors.gray.300'),
            '--tw-prose-invert-pre-bg': 'rgb(0 0 0 / 50%)',
            '--tw-prose-invert-th-borders': theme('colors.gray.300'),
            '--tw-prose-invert-td-borders': theme('colors.gray.300'),
          },
        },
        dark: {
          css: {
            '--tw-prose-body': theme('colors.gray.500'),
            '--tw-prose-headings': theme('colors.gray.500'),
            '--tw-prose-lead': theme('colors.gray.500'),
            '--tw-prose-links': theme('colors.gray.500'),
            '--tw-prose-bold': theme('colors.gray.500'),
            '--tw-prose-counters': theme('colors.gray.500'),
            '--tw-prose-bullets': theme('colors.gray.500'),
            '--tw-prose-hr': theme('colors.gray.500'),
            '--tw-prose-quotes': theme('colors.gray.500'),
            '--tw-prose-quote-borders': theme('colors.gray.500'),
            '--tw-prose-captions': theme('colors.gray.500'),
            '--tw-prose-code': theme('colors.gray.500'),
            '--tw-prose-pre-code': theme('colors.gray.500'),
            '--tw-prose-pre-bg': theme('colors.gray.500'),
            '--tw-prose-th-borders': theme('colors.gray.500'),
            '--tw-prose-td-borders': theme('colors.gray.500'),
            '--tw-prose-invert-body': theme('colors.gray.500'),
            '--tw-prose-invert-headings': theme('colors.gray.500'),
            '--tw-prose-invert-lead': theme('colors.gray.500'),
            '--tw-prose-invert-links': theme('colors.black'),
            '--tw-prose-invert-bold': theme('colors.black'),
            '--tw-prose-invert-counters': theme('colors.gray.500'),
            '--tw-prose-invert-bullets': theme('colors.gray.500'),
            '--tw-prose-invert-hr': theme('colors.gray.500'),
            '--tw-prose-invert-quotes': theme('colors.gray.500'),
            '--tw-prose-invert-quote-borders': theme('colors.gray.500'),
            '--tw-prose-invert-captions': theme('colors.gray.500'),
            '--tw-prose-invert-code': theme('colors.gray.500'),
            '--tw-prose-invert-pre-code': theme('colors.gray.500'),
            '--tw-prose-invert-pre-bg': 'rgb(0 0 0 / 50%)',
            '--tw-prose-invert-th-borders': theme('colors.gray.500'),
            '--tw-prose-invert-td-borders': theme('colors.gray.500'),
          },
        },
        content: {
          css: {
            blockquote: {
              lineHeight: 1.5,
              fontFamily: theme('fontFamily.condensed'),
              fontWeight: theme('fontWeight.bold'),
              fontStyle: 'normal'
            },
            cite: {
              marginTop: '1rem',
              textTransform: 'uppercase',
              fontStyle: 'normal',
              fontSize: '0.6em'
            },
            '--tw-prose-body': theme('colors.gray.600'),
            '--tw-prose-headings': theme('colors.gray.600'),
            '--tw-prose-lead': theme('colors.gray.600'),
            '--tw-prose-links': theme('colors.gray.600'),
            '--tw-prose-bold': theme('colors.gray.600'),
            '--tw-prose-counters': theme('colors.primary'),
            '--tw-prose-bullets': theme('colors.primary'),
            '--tw-prose-hr': theme('colors.gray.600'),
            '--tw-prose-quotes': theme('colors.gray.600'),
            '--tw-prose-quote-borders': theme('colors.primary'),
            '--tw-prose-captions': theme('colors.gray.600'),
            '--tw-prose-code': theme('colors.gray.600'),
            '--tw-prose-pre-code': theme('colors.gray.600'),
            '--tw-prose-pre-bg': theme('colors.gray.600'),
            '--tw-prose-th-borders': theme('colors.gray.600'),
            '--tw-prose-td-borders': theme('colors.gray.600'),
            '--tw-prose-invert-body': theme('colors.gray.600'),
            '--tw-prose-invert-headings': theme('colors.gray.600'),
            '--tw-prose-invert-lead': theme('colors.gray.600'),
            '--tw-prose-invert-links': theme('colors.black'),
            '--tw-prose-invert-bold': theme('colors.black'),
            '--tw-prose-invert-counters': theme('colors.gray.600'),
            '--tw-prose-invert-bullets': theme('colors.gray.600'),
            '--tw-prose-invert-hr': theme('colors.gray.600'),
            '--tw-prose-invert-quotes': theme('colors.gray.600'),
            '--tw-prose-invert-quote-borders': theme('colors.gray.600'),
            '--tw-prose-invert-captions': theme('colors.gray.600'),
            '--tw-prose-invert-code': theme('colors.gray.600'),
            '--tw-prose-invert-pre-code': theme('colors.gray.600'),
            '--tw-prose-invert-pre-bg': 'rgb(0 0 0 / 50%)',
            '--tw-prose-invert-th-borders': theme('colors.gray.600'),
            '--tw-prose-invert-td-borders': theme('colors.gray.600'),
          },
        },
        '2xl': {
          css: {
            fontSize: '1.375rem',
            lineHeight: '1.25'
          }
        }
      }),
    },
  },
  plugins: [
    /** Mask Clip */
    plugin(function({ addBase, addUtilities, matchUtilities, theme }) {
        addBase({
            ':root': { 
                '--tw-mask-w': '16px',
                '--tw-mask-h': '20px',
                '--tw-mask-position-x': 0,
                '--tw-mask-position-y': 0,
                '--tw-mask-clip': 'border-box'
            }
        })
        matchUtilities(
            {
                mask: (value) => {
                    let [a,b] = value
                    let final = {
                        mask: a + ' var(--tw-mask-position-x) var(--tw-mask-position-y) / var(--tw-mask-w) var(--tw-mask-h), linear-gradient(#fff,#fff)',
                        maskComposite: 'exclude',
                        maskClip: 'var(--tw-mask-clip)',
                        maskRepeat: 'no-repeat'
                    }

                    for (const [key, i] of Object.entries(b)) {
                        final[key] = i
                    }

                    return final
                },
            },
            { values: theme('mask') }
        )
        matchUtilities(
            {
                'mask-w': (value) => ({
                    '--tw-mask-w': value,
                }),
            },
            { values: theme('spacing') }
        ),
        matchUtilities(
            {
                'mask-h': (value) => ({
                    '--tw-mask-h': value,
                }),
            },
            { values: theme('spacing') }
        )
        matchUtilities(
            {
                'mask-clip': (value) => ({
                    '--tw-mask-clip': value,
                }),
            },
            { values: theme('maskClip') }
        )
        addUtilities({
          '.clip-logo': {
            'clip-path': 'url(#logo-clip)',
          }
        })
    }),
    plugin(function({matchUtilities, theme}) {
        matchUtilities(
            {
                'text-shadow': (value) => ({
                    'text-shadow': value,
                })
            },
            { values: theme('textShadow') }
        )
    }),
    plugin(function({ matchVariant }) {
      matchVariant(
        'gf',
        (value) => {
          return `&>.gform_wrapper ${value}`;
        },
        {
          values: {
            header: '.gform_heading',
            footer: '.gform_footer',
            title: '.gform_title',
            description: '.gform_description',
            form: 'form',
            fields: '.gform_fields',
            field: '.gfield',
            'field-container': '.gfield .ginput_container',
            'file-container': '.gfield .ginput_container_fileupload',
            'file': '.gfield input[type="file"]',
            fieldset: 'fieldset.gfield',
            legend: 'fieldset.gfield > legend',
            inputs: '.ginput_container :is(input, textarea)',
            placeholder: '.ginput_container :is(input, textarea)::placeholder',
            input: '.ginput_container input',
            textarea: '.ginput_container textarea',
            text: '.ginput_container input[type="text"]',
            radio: '.ginput_container .gfield_radio',
            check: '.ginput_container .gfield_checkbox',
            choice: '.ginput_container .gchoice',
            'choice-input': '.gchoice > input',
            'choice-label': '.gchoice > label',
            'checked-label': '.gfield-choice-input:checked + label',
            label: '.gfield_label',
            submit: 'input[type="submit"].gform_button',
            required: '.gfield_required'
          }
        }
      )
    }),
    require('@tailwindcss/typography')
  ],
  future: {
    hoverOnlyWhenSupported: true,
  },
};
